<template>
  <div class="quote-slider">
    <div class="slider-header">
      {{ $t('components.quote_slider.slider_heading') }}
    </div>
    <div
      v-if="allAmounts.length > 1"
      class="slider"
    >
      <brazil-v-slider
        :min="min"
        :max="max"
        :step="step"
        :value="selectedAmount"
        @valueChanged="amountChanged"
      />
      <span class="slider-info min">R$ {{ min }}</span>
      <span class="slider-info max">R$ {{ max }}</span>
    </div>
    <div
      v-if="allAmounts.length == 1"
      class="amount"
    >
      <span>R$ {{ min }}</span>
    </div>
    <div class="slider-header installments-header">
      {{ $t('components.quote_slider.installments_heading') }}
    </div>
    <div class="installments">
      <span
        v-for="(i, index) in allInstallments"
        :key="index"
        :class="{
          'installment':true,
          'active':(i === selectedInstallments),
          'disabled': !installmentExists(i)
        }"
        @click="setSelectedInstallments(i)"
      >{{ i }}X</span>
    </div>
  </div>
</template>
<script>
import BrazilVSlider from './BrazilVSlider.vue';

export default {
  components: {
    BrazilVSlider,
  },
  props: {
    quotes: {
      type:     Array,
      required: true,
    },
    installments: {
      type:    Number,
      default: 6,
    },
    amount: {
      type:    Number,
      default: 500,
    },
  },
  data() {
    return {
      selectedInstallments: this.installments,
      selectedAmount:       this.amount,
    };
  },
  computed: {
    allAmounts() {
      return this.lodash.sortedUniq(
        this.lodash.sortBy(
          this.lodash.flatMap(
            this.quotes,
            (q) => parseFloat(q.amount_requested),
          ),
        ),
      );
    },
    availableInstallments() {
      const quotes = this.lodash.filter(
        this.quotes,
        (q) => parseFloat(q.amount_requested) === this.selectedAmount,
      );

      return this.lodash.sortedUniq(
        this.lodash.flatMap(
          this.lodash.sortBy(quotes, ['installments_requested']),
          (q) => q.installments_requested,
        ),
      );
    },
    allInstallments() {
      return [3, 6, 9, 12];
    },
    min() {
      return this.allAmounts[0];
    },
    max() {
      return this.allAmounts[this.allAmounts.length - 1];
    },
    step() {
      if (this.allAmounts.length > 1) {
        return this.allAmounts[1] - this.allAmounts[0];
      }
      return 250;
    },
  },
  mounted() {
    this.updateSelectedAmount();
    this.updateSelectedInstallment();
    this.emitSliderChange();
  },
  methods: {
    updateSelectedAmount() {
      const [firstAmount] = this.allAmounts;
      const lastAmount = this.allAmounts[this.allAmounts.length - 1];
      if (this.selectedAmount < firstAmount) {
        this.selectedAmount = firstAmount;
      } else if (this.selectedAmount > lastAmount) {
        this.selectedAmount = lastAmount;
      }
    },
    updateSelectedInstallment() {
      const [firstInstallment] = this.availableInstallments;
      const lastInstallment = this.availableInstallments[this.availableInstallments.length - 1];
      if (this.selectedInstallments < firstInstallment) {
        this.selectedInstallments = firstInstallment;
      } else if (this.selectedInstallments > lastInstallment) {
        this.selectedInstallments = lastInstallment;
      }
    },
    amountChanged(amount) {
      this.selectedAmount = amount;

      if (this.lodash.includes(this.availableInstallments, this.selectedInstallments)) {
        this.emitSliderChange();
        return;
      }

      this.updateSelectedInstallment();
      this.emitSliderChange();
    },
    setSelectedInstallments(installments) {
      if (!this.installmentExists(installments)) {
        return;
      }

      this.selectedInstallments = installments;
      this.emitSliderChange();
    },
    installmentExists(installments) {
      return this.lodash.includes(this.availableInstallments, installments);
    },
    emitSliderChange() {
      this.$emit('sliderChanged', {
        amount:       this.selectedAmount,
        installments: this.selectedInstallments,
      });
    },
  },
};
</script>
<style lang="scss" module>
  .quote-slider {
    width: 100%;
    padding: 18px;
    box-shadow: 10px 3px 23px 2px rgba(0, 0, 0, 0.13);
    border-radius: 14px;
    border: 1px solid #c5c5c5;
  }
  .slider-header {
    font-family: "Bree Serif", serif;
    font-size: 19px;
    color: #333333;
    font-weight: 400;
    line-height: 1.2;
    margin-bottom: .6rem;
    text-align: center;
  }
  .slider {
    margin-top: 38px;
    position: relative;
    padding: 12px 0 14px 0;
    background-color: #f3f3f3;
    border-radius: 4px;
  }
  .slider-info {
    position: absolute;
    bottom: 12px;
    font-weight: bold;
    color: #4a4a4a;
    font-size: 14px;
  }
  .slider-info.min {
    left: 10px;
  }
  .slider-info.max {
    right: 10px;
  }
  .installments-header {
    margin-bottom: 12px;
    margin-top: 24px;
  }
  .installments {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }
  .installments .installment {
    padding: 8px;
    width: 23%;
    background-color: #f3f3f3;
    color: rgba(0,0,0,0.5);
    cursor: pointer;
    font-weight: bold;
    font-size: 2em;
    text-align: center;
    text-transform: none;
    letter-spacing: 0;
    border-radius: 6px;
  }
  .installments .installment.active, .installments .installment:hover {
    color: white;
    background: #048a05;
  }
  .installments .installment.disabled, .installments .installment.disabled:hover {
    cursor: default;
    opacity: .6;
    background-color: #f5f5f5;
    color: rgba(17, 17, 17, 0.5);
  }
  .amount {
    margin-top: 1.5rem;
    text-align: center;
  }
  .amount span {
    text-align: center;
    color: #048a05;
    font-weight: bold;
    font-size: 1.5rem;
    padding: 16px 24px;
    border: 1px solid #f5bf05;
    border-radius: 14px;
  }
  @media screen and (min-width: 700px) {
    .slider-header {
      text-align: left;
    }
  }
</style>
